import { useState, useEffect, useCallback } from 'react';
import { systemHealthAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { CheckCircle, Warning, XCircle, Heartbeat, SpinnerGap, ArrowClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

const STATUS = {
  pass: { icon: CheckCircle, color: 'text-green-600', badge: 'bg-green-100 text-green-800', label: 'OK' },
  warn: { icon: Warning, color: 'text-amber-500', badge: 'bg-amber-100 text-amber-800', label: 'Needs setup' },
  fail: { icon: XCircle, color: 'text-red-600', badge: 'bg-red-100 text-red-800', label: 'Problem' },
};

const OVERALL_TEXT = {
  pass: 'Everything is working',
  warn: 'Working: some services are not set up yet',
  fail: 'Something needs attention',
};

const CheckRow = ({ c, i }) => {
  const s = STATUS[c.status];
  const Icon = s.icon;
  return (
    <div className="flex items-start gap-3 py-3 border-b border-slate-100 last:border-0" data-testid={`health-check-row-${i}`}>
      <Icon size={22} weight="fill" className={`${s.color} shrink-0 mt-0.5`} />
      <div className="flex-1">
        <p className="font-medium text-slate-900">{c.name}</p>
        <p className="text-sm text-slate-500">{c.detail}</p>
      </div>
      <Badge className={s.badge}>{s.label}</Badge>
    </div>
  );
};

const RunResult = ({ run }) => {
  const groups = run.checks.reduce((acc, c) => ({ ...acc, [c.category]: [...(acc[c.category] || []), c] }), {});
  const s = STATUS[run.overall];
  const Icon = s.icon;
  return (
    <div className="space-y-6">
      <Card className={run.overall === 'fail' ? 'border-red-300' : run.overall === 'pass' ? 'border-green-300' : 'border-amber-300'}>
        <CardContent className="py-5 flex items-center gap-4">
          <Icon size={40} weight="fill" className={s.color} />
          <div>
            <p className="text-xl font-semibold text-slate-900" data-testid="health-check-overall">{OVERALL_TEXT[run.overall]}</p>
            <p className="text-sm text-slate-500">
              {run.trigger === 'scheduled' ? 'Weekly automatic check' : 'Manual check'} on {new Date(run.finished_at).toLocaleString()}
            </p>
          </div>
        </CardContent>
      </Card>
      {Object.entries(groups).map(([cat, checks]) => (
        <Card key={cat}>
          <CardHeader className="pb-1"><CardTitle className="text-base">{cat}</CardTitle></CardHeader>
          <CardContent>{checks.map((c) => <CheckRow key={c.name} c={c} i={run.checks.indexOf(c)} />)}</CardContent>
        </Card>
      ))}
    </div>
  );
};

export default function AdminHealthCheckPage() {
  const [runs, setRuns] = useState([]);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await systemHealthAPI.history();
      setRuns(res.data.runs || []);
    } catch (error) {
      toast.error('Failed to load health check history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    try {
      setRunning(true);
      await systemHealthAPI.run();
      toast.success('Health check finished');
      load();
    } catch (error) {
      toast.error('Health check could not run');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-health-check-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-lg"><Heartbeat size={28} className="text-teal-600" /></div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>System Health</h1>
            <p className="text-slate-500 text-sm">Safe checks only: nothing is created or changed. Runs automatically every Monday at 8am Eastern and emails admins if something fails.</p>
          </div>
        </div>
        <Button onClick={runNow} disabled={running} className="bg-teal-600 hover:bg-teal-700" data-testid="health-check-run-btn">
          {running ? <SpinnerGap size={18} className="animate-spin mr-2" /> : <ArrowClockwise size={18} className="mr-2" />}
          {running ? 'Checking...' : 'Run check now'}
        </Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><SpinnerGap size={32} className="animate-spin text-teal-600" /></div>
      ) : runs.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-500" data-testid="health-check-empty">No checks yet. Click "Run check now".</CardContent></Card>
      ) : (
        <>
          <RunResult run={runs[0]} />
          {runs.length > 1 && (
            <Card className="mt-6">
              <CardHeader className="pb-1"><CardTitle className="text-base">Recent checks</CardTitle></CardHeader>
              <CardContent>
                {runs.slice(1).map((r) => (
                  <div key={r.id} className="flex items-center justify-between py-2 text-sm border-b border-slate-100 last:border-0" data-testid={`health-check-history-${r.id}`}>
                    <span>{new Date(r.finished_at).toLocaleString()} • {r.trigger === 'scheduled' ? 'Weekly' : 'Manual'}</span>
                    <Badge className={STATUS[r.overall].badge}>{OVERALL_TEXT[r.overall]}</Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
